/**
 * Coastal Reflections – Find Your Print Quiz Widget
 *
 * Standalone IIFE — no dependencies, no build step.
 * Inject quiz.js + quiz.css into your Shopify theme, then add a trigger button.
 *
 * Trigger:  <button data-cr-quiz>Find Your Print</button>
 * Deep link: https://yourstore.com/?quiz=open
 */
(function () {
  "use strict";

  // ---------------------------------------------------------------------------
  // Configuration
  // ---------------------------------------------------------------------------
  var CONFIG = {
    shopDomain: window.Shopify ? window.Shopify.shop : window.location.hostname,
    productsPerPage: 250, // /products.json max
    resultLimit: 24,
    fallbackSteps: 3,   // how many trailing questions to drop before scoring fallback
  };

  var QUESTIONS = [
    {
      id: "mood",
      tagPrefix: "mood:",
      label: "What feeling do you want the print to bring?",
      subtitle: "Choose as many as you like.",
      options: [
        { value: "calm",      label: "Calm & Serene",   emoji: "🌊" },
        { value: "energetic", label: "Energetic",        emoji: "⚡" },
        { value: "dramatic",  label: "Dramatic",         emoji: "🌩️" },
        { value: "golden",    label: "Golden Hour",      emoji: "🌅" },
      ],
    },
    {
      id: "subject",
      tagPrefix: "subject:",
      label: "What kind of scene appeals to you?",
      subtitle: "Choose as many as you like.",
      options: [
        { value: "surf",        label: "Surf",        emoji: "🏄" },
        { value: "aerial",      label: "Aerial",      emoji: "🚁" },
        { value: "beach",       label: "Beach",       emoji: "🏖️" },
        { value: "rocks",       label: "Rocks",       emoji: "🪨" },
        { value: "pier",        label: "Pier",        emoji: "🌉" },
        { value: "lighthouse",  label: "Lighthouse",  emoji: "🗼" },
        { value: "pool",        label: "Rock Pool",   emoji: "🌀" },
        { value: "tropical",    label: "Tropical",    emoji: "🌴" },
      ],
    },
    {
      id: "location",
      tagPrefix: "location:",
      label: "Any preferred coastal region?",
      subtitle: "Skip if you have no preference.",
      optional: true,
      options: [
        { value: "qld",            label: "Queensland",     emoji: "☀️" },
        { value: "nsw",            label: "New South Wales", emoji: "🦘" },
        { value: "vic",            label: "Victoria",       emoji: "🌿" },
        { value: "tas",            label: "Tasmania",       emoji: "❄️" },
        { value: "international",  label: "International",  emoji: "✈️" },
      ],
    },
    {
      id: "palette",
      tagPrefix: "palette:",
      label: "What colour palette suits your space?",
      subtitle: "Choose as many as you like.",
      options: [
        { value: "warm",    label: "Warm Tones",   emoji: "🟠" },
        { value: "cool",    label: "Cool Tones",   emoji: "🔵" },
      ],
    },
    {
      id: "size",
      tagPrefix: "size:",
      label: "What size are you thinking?",
      subtitle: "Choose as many as you like.",
      options: [
        { value: "small-medium", label: "Small–Medium", emoji: "🖼️", desc: "A3 · A2 · A1" },
        { value: "large",        label: "Large",        emoji: "🖼️", desc: "100×70 cm" },
        { value: "extra-large",  label: "Extra Large",  emoji: "🖼️", desc: "A0" },
      ],
    },
    {
      id: "orient",
      tagPrefix: "orient:",
      label: "Portrait or landscape?",
      subtitle: "Skip if you have no preference.",
      optional: true,
      options: [
        { value: "landscape", label: "Landscape", emoji: "🏞️" },
        { value: "portrait",  label: "Portrait",  emoji: "🖼️" },
      ],
    },
  ];

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  var state = {
    step: 0,
    selections: {},   // { mood: Set, subject: Set, … }
    allProducts: [],
    loaded: false,
    loading: false,
    open: false,
  };

  // Initialise selections
  QUESTIONS.forEach(function (q) {
    state.selections[q.id] = new Set();
  });

  // ---------------------------------------------------------------------------
  // Product loading  (/products.json, paginated)
  // ---------------------------------------------------------------------------
  var _loadCallbacks = [];

  function loadProducts(callback) {
    if (state.loaded) { if (callback) callback(); return; }
    if (callback) _loadCallbacks.push(callback);
    if (state.loading) { return; }   // already in flight — callback queued above
    state.loading = true;

    var products = [];
    var page = 1;

    function fetchPage() {
      var url =
        "/products.json?limit=" +
        CONFIG.productsPerPage +
        "&page=" +
        page;

      fetch(url)
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var batch = data.products || [];
          products = products.concat(batch);
          if (batch.length === CONFIG.productsPerPage) {
            page++;
            fetchPage();
          } else {
            state.allProducts = products;
            state.loaded = true;
            state.loading = false;
            var cbs = _loadCallbacks.splice(0);
            cbs.forEach(function (cb) { cb(); });
          }
        })
        .catch(function (err) {
          console.warn("[CR Quiz] Failed to load products:", err);
          state.loaded = true;
          state.loading = false;
          var cbs = _loadCallbacks.splice(0);
          cbs.forEach(function (cb) { cb(); });
        });
    }

    fetchPage();
  }

  // ---------------------------------------------------------------------------
  // Filtering
  // ---------------------------------------------------------------------------
  function tagsFor(product) {
    var raw = product.tags || [];
    var arr = Array.isArray(raw) ? raw : raw.split(", ");
    var set = {};
    arr.forEach(function (t) { set[t.trim()] = true; });
    return set;
  }

  function productMatchesSelections(product, skipSteps) {
    skipSteps = skipSteps || 0;
    var tagSet = tagsFor(product);
    var stepsToCheck = QUESTIONS.slice(0, QUESTIONS.length - skipSteps);
    for (var i = 0; i < stepsToCheck.length; i++) {
      var q = stepsToCheck[i];
      var sel = state.selections[q.id];
      if (!sel || sel.size === 0) continue;
      var matches = false;
      sel.forEach(function (val) { if (tagSet[q.tagPrefix + val]) matches = true; });
      if (!matches) return false;
    }
    return true;
  }

  function scoreProduct(product) {
    var tagSet = tagsFor(product);
    var score = 0;
    QUESTIONS.forEach(function (q) {
      var sel = state.selections[q.id];
      if (!sel || sel.size === 0) return;
      sel.forEach(function (val) { if (tagSet[q.tagPrefix + val]) score++; });
    });
    return score;
  }

  function locationPool() {
    // Returns products matching the selected location(s), or all products if none selected.
    var sel = state.selections["location"];
    if (!sel || sel.size === 0) return state.allProducts;
    var matches = state.allProducts.filter(function (p) {
      var tagSet = tagsFor(p);
      var ok = false;
      sel.forEach(function (val) { if (tagSet["location:" + val]) ok = true; });
      return ok;
    });
    return matches.length > 0 ? matches : state.allProducts;
  }

  function getResults() {
    // Phase 1: strict AND on all answered questions, progressively drop trailing ones.
    // Always search within the location pool so non-QLD prints never appear for QLD queries.
    var pool = locationPool();
    for (var skip = 0; skip <= CONFIG.fallbackSteps; skip++) {
      var strict = pool.filter(function (p) {
        return productMatchesSelections(p, skip);
      });
      if (strict.length > 0) return { products: strict, mode: "exact", skipped: skip };
    }

    // Phase 2: score within location pool — never cross location boundaries.
    var scored = pool
      .map(function (p) { return { product: p, score: scoreProduct(p) }; })
      .filter(function (s) { return s.score > 0; })
      .sort(function (a, b) { return b.score - a.score; });

    if (scored.length > 0) {
      return { products: scored.map(function (s) { return s.product; }), mode: "scored" };
    }

    // Phase 3: show all location-pool products unranked (e.g. location selected but nothing else)
    if (pool.length < state.allProducts.length) {
      return { products: pool, mode: "location-only" };
    }

    // Ultimate fallback
    return { products: state.allProducts, mode: "all" };
  }

  // ---------------------------------------------------------------------------
  // DOM helpers
  // ---------------------------------------------------------------------------
  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        if (k === "className") node.className = attrs[k];
        else if (k === "innerHTML") node.innerHTML = attrs[k];
        else node.setAttribute(k, attrs[k]);
      });
    }
    if (children) {
      children.forEach(function (c) {
        if (c) node.appendChild(typeof c === "string" ? document.createTextNode(c) : c);
      });
    }
    return node;
  }

  // ---------------------------------------------------------------------------
  // Modal shell (injected once)
  // ---------------------------------------------------------------------------
  var modal, overlay, modalInner;

  function injectModal() {
    if (document.getElementById("cr-quiz-overlay")) return;

    overlay = el("div", { id: "cr-quiz-overlay", "aria-hidden": "true" });
    modal = el("div", { id: "cr-quiz-modal", role: "dialog", "aria-modal": "true", "aria-label": "Find Your Print" });

    var closeBtn = el("button", { id: "cr-quiz-close", "aria-label": "Close quiz", type: "button" }, ["×"]);
    closeBtn.addEventListener("click", closeQuiz);

    modalInner = el("div", { id: "cr-quiz-inner" });
    modal.appendChild(closeBtn);
    modal.appendChild(modalInner);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    overlay.addEventListener("click", function (e) {
      if (e.target === overlay) closeQuiz();
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && state.open) closeQuiz();
    });
  }

  // ---------------------------------------------------------------------------
  // Open / close
  // ---------------------------------------------------------------------------
  function openQuiz() {
    injectModal();
    state.open = true;
    state.step = 0;
    QUESTIONS.forEach(function (q) { state.selections[q.id] = new Set(); });
    overlay.removeAttribute("aria-hidden");
    overlay.classList.add("cr-quiz--visible");
    document.body.style.overflow = "hidden";

    // Update URL without navigation
    if (history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set("quiz", "open");
      history.replaceState(null, "", url.toString());
    }

    renderStep();

    // Start loading products in background immediately
    loadProducts(function () {});
  }

  function closeQuiz() {
    state.open = false;
    overlay.classList.remove("cr-quiz--visible");
    overlay.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";

    if (history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.delete("quiz");
      history.replaceState(null, "", url.toString());
    }
  }

  // ---------------------------------------------------------------------------
  // Render step
  // ---------------------------------------------------------------------------
  function renderStep() {
    modalInner.innerHTML = "";

    if (state.step >= QUESTIONS.length) {
      renderResults();
      return;
    }

    var q = QUESTIONS[state.step];
    var progress = Math.round(((state.step) / QUESTIONS.length) * 100);

    // Progress bar
    var progressBar = el("div", { className: "cr-quiz__progress" }, [
      el("div", { className: "cr-quiz__progress-fill", style: "width:" + progress + "%" }),
    ]);

    // Step counter
    var counter = el("p", { className: "cr-quiz__counter" }, [
      "Question " + (state.step + 1) + " of " + QUESTIONS.length,
    ]);

    // Heading
    var heading = el("h2", { className: "cr-quiz__question" }, [q.label]);

    // Subtitle
    var subtitle = el("p", { className: "cr-quiz__subtitle" }, [
      q.optional ? (q.subtitle || "Skip if you have no preference.") : (q.subtitle || "Choose as many as you like."),
    ]);

    // Options grid
    var optGrid = el("div", { className: "cr-quiz__options" });
    q.options.forEach(function (opt) {
      var chip = el("button", {
        className: "cr-quiz__chip" + (state.selections[q.id].has(opt.value) ? " cr-quiz__chip--selected" : ""),
        type: "button",
        "data-value": opt.value,
      });

      var chipInner = el("span", { className: "cr-quiz__chip-inner" }, [
        el("span", { className: "cr-quiz__chip-emoji" }, [opt.emoji]),
        el("span", { className: "cr-quiz__chip-label" }, [opt.label]),
      ]);

      if (opt.desc) {
        chipInner.appendChild(el("span", { className: "cr-quiz__chip-desc" }, [opt.desc]));
      }

      chip.appendChild(chipInner);

      chip.addEventListener("click", function () {
        var val = this.getAttribute("data-value");
        if (state.selections[q.id].has(val)) {
          state.selections[q.id].delete(val);
          this.classList.remove("cr-quiz__chip--selected");
        } else {
          state.selections[q.id].add(val);
          this.classList.add("cr-quiz__chip--selected");
        }
        updateNavButtons();
      });

      optGrid.appendChild(chip);
    });

    // Navigation
    var navRow = el("div", { className: "cr-quiz__nav" });

    if (state.step > 0) {
      var backBtn = el("button", { className: "cr-quiz__btn cr-quiz__btn--back", type: "button" }, ["← Back"]);
      backBtn.addEventListener("click", function () {
        state.step--;
        renderStep();
      });
      navRow.appendChild(backBtn);
    }

    var nextLabel = state.step === QUESTIONS.length - 1 ? "Show my prints →" : "Next →";
    var nextBtn = el("button", {
      id: "cr-quiz-next",
      className: "cr-quiz__btn cr-quiz__btn--next",
      type: "button",
    }, [nextLabel]);

    nextBtn.addEventListener("click", function () {
      state.step++;
      renderStep();
    });

    // Skip button for optional questions
    if (q.optional) {
      var skipBtn = el("button", { className: "cr-quiz__btn cr-quiz__btn--skip", type: "button" }, ["Skip"]);
      skipBtn.addEventListener("click", function () {
        state.selections[q.id] = new Set();
        state.step++;
        renderStep();
      });
      navRow.appendChild(skipBtn);
    }

    navRow.appendChild(nextBtn);

    modalInner.appendChild(progressBar);
    modalInner.appendChild(counter);
    modalInner.appendChild(heading);
    modalInner.appendChild(subtitle);
    modalInner.appendChild(optGrid);
    modalInner.appendChild(navRow);

    updateNavButtons();
  }

  function updateNavButtons() {
    // Next button is always enabled (skip selection = no filter on that dimension)
  }

  // ---------------------------------------------------------------------------
  // Render results
  // ---------------------------------------------------------------------------
  function renderResults() {
    modalInner.innerHTML = "";

    // Show loading state while products fetch
    if (!state.loaded) {
      var loading = el("div", { className: "cr-quiz__loading" }, [
        el("p", {}, ["Finding your prints…"]),
      ]);
      modalInner.appendChild(loading);

      loadProducts(function () { renderResults(); });
      return;
    }

    var result = getResults();
    var products = result.products.slice(0, CONFIG.resultLimit);

    // Header
    var heading = el("h2", { className: "cr-quiz__results-heading" }, [
      "Your prints",
    ]);

    var subMsg = "";
    if (result.mode === "exact" && result.skipped === 0) {
      subMsg = products.length + " print" + (products.length !== 1 ? "s" : "") + " match your preferences.";
    } else if (result.mode === "exact") {
      subMsg = "We loosened the search a little — here are your closest matches.";
    } else if (result.mode === "scored") {
      subMsg = "No exact match — here are the closest prints to your preferences.";
    } else if (result.mode === "location-only") {
      subMsg = "Here are all prints from your chosen region.";
    } else {
      subMsg = "Here's the full collection — explore and find what speaks to you.";
    }

    var sub = el("p", { className: "cr-quiz__results-sub" }, [subMsg]);

    // Grid
    var grid = el("div", { className: "cr-quiz__results-grid" });
    products.forEach(function (product) {
      var image = product.images && product.images[0] ? product.images[0].src : "";
      var thumbUrl = image ? image.replace(/\.(jpg|jpeg|png|webp)(\?|$)/, "_400x.$1$2") : "";
      var url = "/products/" + product.handle;

      var card = el("a", { className: "cr-quiz__card", href: url });

      if (thumbUrl) {
        var img = el("img", {
          className: "cr-quiz__card-img",
          src: thumbUrl,
          alt: product.title,
          loading: "lazy",
        });
        card.appendChild(img);
      } else {
        card.appendChild(el("div", { className: "cr-quiz__card-img cr-quiz__card-img--placeholder" }));
      }

      var cardBody = el("div", { className: "cr-quiz__card-body" }, [
        el("p", { className: "cr-quiz__card-title" }, [product.title]),
        el("p", { className: "cr-quiz__card-price" }, [formatPrice(product)]),
      ]);

      card.appendChild(cardBody);
      grid.appendChild(card);
    });

    // Restart
    var restartBtn = el("button", { className: "cr-quiz__btn cr-quiz__btn--restart", type: "button" }, [
      "Start again",
    ]);
    restartBtn.addEventListener("click", function () {
      state.step = 0;
      QUESTIONS.forEach(function (q) { state.selections[q.id] = new Set(); });
      renderStep();
    });

    modalInner.appendChild(heading);
    modalInner.appendChild(sub);
    modalInner.appendChild(grid);
    modalInner.appendChild(restartBtn);
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------
  function formatPrice(product) {
    if (!product.variants || !product.variants[0]) return "";
    var price = parseFloat(product.variants[0].price);
    if (isNaN(price)) return "";
    return "From $" + price.toFixed(0);
  }

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------
  function attachTriggers() {
    document.querySelectorAll("[data-cr-quiz]").forEach(function (btn) {
      btn.addEventListener("click", openQuiz);
    });
  }

  function init() {
    injectModal();
    attachTriggers();

    // Re-attach when Shopify sections re-render
    document.addEventListener("shopify:section:load", attachTriggers);

    // Deep link support
    var params = new URLSearchParams(window.location.search);
    if (params.get("quiz") === "open") {
      openQuiz();
    }

    // Preload products after a short idle delay
    if ("requestIdleCallback" in window) {
      requestIdleCallback(function () { loadProducts(function () {}); });
    } else {
      setTimeout(function () { loadProducts(function () {}); }, 2000);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
